import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowUpRight,
  Bug,
  Clock as ClockIcon,
  Lock,
  MessageSquare,
  Pencil,
  ShieldCheck,
  Send,
  StickyNote,
  TriangleAlert,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, Checkbox, Select, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useAssignTicket,
  useCannedResponses,
  useConvertTicket,
  useEscalateTicket,
  useLogTicketTime,
  useProjects,
  useReplyToTicket,
  useSetTicketStatus,
  useTicket,
} from './api';
import { TicketDialog } from './ticket-dialog';
import {
  SLA_COLOUR_CLASS,
  SLA_DOT_CLASS,
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_VARIANT,
  TICKET_SOURCE_LABELS,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_VARIANT,
  TICKET_TYPE_LABELS,
  formatMinutes,
  parseHoursInput,
  timeUntil,
} from './labels';
import type { Ticket, TicketStatus } from './types';

const STATUSES: TicketStatus[] = [
  'NEW',
  'OPEN',
  'IN_PROGRESS',
  'PENDING_CUSTOMER',
  'PENDING_THIRD_PARTY',
  'RESOLVED',
  'CLOSED',
];

export function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const { data: ticket, isLoading } = useTicket(ticketId);
  const [editing, setEditing] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [converting, setConverting] = useState(false);

  if (isLoading) return <TableSkeleton columns={3} />;
  if (!ticket) return null;

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: 'Service desk', to: '/service/tickets' },
          { label: ticket.number },
        ]}
        title={ticket.subject}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-xs">{ticket.number}</span>
            <span>·</span>
            <span>{ticket.company.name}</span>
            <span>·</span>
            <span>{TICKET_TYPE_LABELS[ticket.ticketType]}</span>
            <span>·</span>
            <span>{TICKET_SOURCE_LABELS[ticket.source]}</span>
            {ticket.reopenCount > 0 && (
              <>
                <span>·</span>
                <span className="text-amber-600">
                  reopened {ticket.reopenCount} {ticket.reopenCount === 1 ? 'time' : 'times'}
                </span>
              </>
            )}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={TICKET_PRIORITY_VARIANT[ticket.priority]}>
              {TICKET_PRIORITY_LABELS[ticket.priority]}
            </Badge>
            <Badge variant={TICKET_STATUS_VARIANT[ticket.status]}>
              {TICKET_STATUS_LABELS[ticket.status]}
            </Badge>
            <Can permission="service.ticket.update">
              <Button size="sm" variant="outline" onClick={() => setConverting(true)}>
                <ArrowUpRight className="h-4 w-4" />
                Convert
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEscalating(true)}>
                <TriangleAlert className="h-4 w-4" />
                Escalate
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            </Can>
          </div>
        }
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Conversation ticket={ticket} />
        </div>

        <div className="space-y-5">
          <SlaPanel ticket={ticket} />
          <Entitlement ticket={ticket} />
          <Details ticket={ticket} />
          <TimePanel ticket={ticket} />
        </div>
      </div>

      {editing && <TicketDialog ticket={ticket} onClose={() => setEditing(false)} />}
      {escalating && <EscalateDialog ticket={ticket} onClose={() => setEscalating(false)} />}
      {converting && <ConvertDialog ticket={ticket} onClose={() => setConverting(false)} />}
    </div>
  );
}

function SlaPanel({ ticket }: { ticket: Ticket }) {
  if (!ticket.sla) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          No SLA policy matched this ticket, so it has no deadlines. Configure one under Service
          setup and the clock will start on the next ticket.
        </CardContent>
      </Card>
    );
  }

  const { sla } = ticket;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className={cn('h-2.5 w-2.5 rounded-full', SLA_DOT_CLASS[sla.colour])} />
          SLA
        </CardTitle>
        <CardDescription>
          {sla.policyName ?? 'Policy'} — counted in working hours only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {sla.paused && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs">
            <Lock className="h-3.5 w-3.5" />
            Clock paused while waiting on the client.
            {ticket.pausedBusinessMinutes > 0 && (
              <span className="text-muted-foreground">
                {formatMinutes(ticket.pausedBusinessMinutes)} given back so far
              </span>
            )}
          </div>
        )}

        {sla.firstResponse && (
          <SlaClock
            label="First response"
            dueAt={sla.firstResponse.dueAt}
            met={sla.firstResponse.met}
            breached={sla.firstResponse.breached}
            remaining={sla.firstResponse.percentRemaining}
            respondedAt={ticket.firstRespondedAt}
            paused={sla.paused}
          />
        )}

        <SlaClock
          label="Resolution"
          dueAt={sla.resolution.dueAt}
          met={sla.resolution.met}
          breached={sla.resolution.breached}
          remaining={sla.resolution.percentRemaining}
          respondedAt={ticket.resolvedAt}
          paused={sla.paused}
        />
      </CardContent>
    </Card>
  );
}

function SlaClock({
  label,
  dueAt,
  met,
  breached,
  remaining,
  respondedAt,
  paused,
}: {
  label: string;
  dueAt: string;
  met: boolean | null;
  breached: boolean;
  remaining: number | null;
  respondedAt: string | null;
  paused: boolean;
}) {
  const colour = breached ? 'RED' : remaining !== null && remaining < 50 ? 'AMBER' : 'GREEN';

  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        {respondedAt ? (
          <span className={met ? 'font-medium text-emerald-600' : 'font-medium text-destructive'}>
            {met ? 'Met' : 'Missed'} · {formatDate(respondedAt)}
          </span>
        ) : (
          <span className={cn('font-medium', SLA_COLOUR_CLASS[colour])}>
            {paused ? 'paused' : timeUntil(dueAt)}
          </span>
        )}
      </div>
      {!respondedAt && (
        <>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full',
                colour === 'RED' ? 'bg-destructive' : colour === 'AMBER' ? 'bg-amber-500' : 'bg-emerald-500',
              )}
              style={{ width: `${Math.max(0, Math.min(100, remaining ?? 0))}%` }}
            />
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Due {formatDateTime(dueAt)}
            {remaining !== null && ` · ${remaining}% of the target left`}
          </div>
        </>
      )}
    </div>
  );
}

function Entitlement({ ticket }: { ticket: Ticket }) {
  if (!ticket.contract) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          No support contract on this client. Anything billable here is ad-hoc.
        </CardContent>
      </Card>
    );
  }

  const bank = ticket.entitlement;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Entitlement
        </CardTitle>
        <CardDescription>
          <Link
            to={`/service/contracts/${ticket.contract.id}`}
            className="text-link hover:underline"
          >
            {ticket.contract.number}
          </Link>{' '}
          {ticket.contract.name} · ends {formatDate(ticket.contract.endDate)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {bank && (
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-muted-foreground">Support hours this period</span>
              <span className={cn('tabular font-medium', bank.overrun && 'text-destructive')}>
                {bank.remainingHours}h left of {(bank.allocatedHours ?? 0) + (bank.carriedForwardHours ?? 0)}h
              </span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  'h-full rounded-full',
                  (bank.consumedPercent ?? 0) >= 100
                    ? 'bg-destructive'
                    : (bank.consumedPercent ?? 0) >= 80
                      ? 'bg-amber-500'
                      : 'bg-primary',
                )}
                style={{ width: `${Math.min(100, bank.consumedPercent ?? 0)}%` }}
              />
            </div>
          </div>
        )}

        {ticket.contract.coverageIncluded && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Covered
            </div>
            <p className="mt-0.5 whitespace-pre-line text-sm">{ticket.contract.coverageIncluded}</p>
          </div>
        )}
        {ticket.contract.coverageExcluded && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Not covered
            </div>
            <p className="mt-0.5 whitespace-pre-line text-sm text-muted-foreground">
              {ticket.contract.coverageExcluded}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Details({ ticket }: { ticket: Ticket }) {
  const assign = useAssignTicket();
  const setStatus = useSetTicketStatus(ticket.id);
  const { data: users = [] } = useUserOptions();

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Can permission="service.ticket.assign">
          <div className="space-y-1.5">
            <Label className="text-xs">Assignee</Label>
            <Select
              className="h-8 text-sm"
              value={ticket.assigneeId ?? ''}
              onChange={(event) =>
                assign.mutate({ id: ticket.id, assigneeId: event.target.value || null })
              }
            >
              <option value="">Unassigned</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </div>
        </Can>

        <Can permission="service.ticket.update">
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select
              className="h-8 text-sm"
              value={ticket.status}
              onChange={(event) => setStatus.mutate(event.target.value)}
            >
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {TICKET_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
            <p className="text-xs text-muted-foreground">
              Waiting on the client stops the SLA clock. Waiting on a supplier does not.
            </p>
          </div>
        </Can>

        <div className="space-y-1 border-t pt-3">
          <Row label="Raised" value={formatDateTime(ticket.createdAt)} />
          {ticket.contact && (
            <Row
              label="Contact"
              value={`${ticket.contact.firstName} ${ticket.contact.lastName ?? ''}`}
            />
          )}
          {ticket.category && <Row label="Category" value={ticket.category} />}
          {ticket.project && (
            <Row
              label="Project"
              value={
                <Link
                  to={`/delivery/projects/${ticket.project.id}`}
                  className="text-link hover:underline"
                >
                  {ticket.project.code}
                </Link>
              }
            />
          )}
          {ticket.escalatedAt && (
            <Row label="Escalated" value={formatDate(ticket.escalatedAt)} />
          )}
          {ticket.csatScore !== null && (
            <Row label="Client rating" value={`${ticket.csatScore} / 5`} />
          )}
        </div>

        {ticket.convertedItems && ticket.convertedItems.length > 0 && (
          <div className="border-t pt-3">
            <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Raised from this
            </div>
            <ul className="space-y-1">
              {ticket.convertedItems.map((item) => (
                <li key={item.id} className="flex items-center gap-1.5 text-sm">
                  <Bug className="h-3.5 w-3.5 text-destructive" />
                  <span className="font-mono text-xs">{item.key}</span>
                  <span className="truncate">{item.title}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TimePanel({ ticket }: { ticket: Ticket }) {
  const logTime = useLogTicketTime(ticket.id);
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [useHourBank, setUseHourBank] = useState(true);
  const minutes = parseHoursInput(hours);
  const hasBank = Boolean(ticket.entitlement);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClockIcon className="h-4 w-4 text-muted-foreground" />
          Time on this ticket
        </CardTitle>
        <CardDescription>
          {formatMinutes(ticket.totalLoggedMinutes ?? 0)} logged
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Can permission="service.ticket.update">
          <div className="flex items-end gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">How long</Label>
              <Input
                className="h-8 w-20"
                placeholder="0.5"
                value={hours}
                onChange={(event) => setHours(event.target.value)}
              />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label className="text-xs">What you did</Label>
              <Input
                className="h-8"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
            <Button
              size="sm"
              disabled={logTime.isPending || minutes === null}
              onClick={async () => {
                if (!minutes) return;
                await logTime.mutateAsync({
                  minutes,
                  description: note || undefined,
                  useHourBank,
                });
                setHours('');
                setNote('');
              }}
            >
              Log
            </Button>
          </div>

          {hasBank && (
            <label className="flex cursor-pointer items-start gap-2 text-xs">
              <Checkbox
                checked={useHourBank}
                onCheckedChange={(value) => setUseHourBank(value === true)}
              />
              <span>
                Draw on the prepaid hour bank
                <span className="block text-muted-foreground">
                  Banked time is not billed again — the client already paid for it.
                </span>
              </span>
            </label>
          )}
        </Can>

        {ticket.timeEntries && ticket.timeEntries.length > 0 && (
          <ul className="space-y-1 border-t pt-3">
            {ticket.timeEntries.map((entry) => (
              <li key={entry.id} className="flex items-center justify-between text-xs">
                <span className="min-w-0 truncate text-muted-foreground">
                  {formatDate(entry.date)} · {entry.user.name}
                  {entry.description && ` — ${entry.description}`}
                </span>
                <span className="tabular ms-2 shrink-0">
                  {formatMinutes(entry.minutes)}
                  {entry.hourBankId && (
                    <span className="ms-1 text-muted-foreground" title="From the hour bank">
                      ◦
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Conversation({ ticket }: { ticket: Ticket }) {
  const reply = useReplyToTicket(ticket.id);
  const { data: canned = [] } = useCannedResponses();
  const [body, setBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [setStatusTo, setSetStatusTo] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Conversation</CardTitle>
        <CardDescription>
          A public reply is what the client sees, and what stops the first-response clock. An
          internal note does neither.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border bg-muted/30 p-3">
          <div className="mb-1 text-xs text-muted-foreground">
            Raised {formatDateTime(ticket.createdAt)}
          </div>
          <p className="whitespace-pre-line text-sm">{ticket.description}</p>
        </div>

        {(ticket.messages ?? []).map((message) => (
          <div
            key={message.id}
            className={cn(
              'rounded-lg border p-3',
              message.isInternal && 'border-amber-200 bg-amber-50/60',
              message.fromClient && 'border-primary/30 bg-primary/5',
            )}
          >
            <div className="mb-1 flex items-center gap-2">
              {message.author && (
                <Avatar name={message.author.name} className="h-5 w-5 text-[9px]" />
              )}
              <span className="text-sm font-medium">
                {message.fromClient ? ticket.company.name : (message.author?.name ?? 'System')}
              </span>
              {message.isInternal && (
                <Badge variant="warning" className="text-[10px]">
                  <StickyNote className="me-1 h-2.5 w-2.5" />
                  internal note
                </Badge>
              )}
              <span className="ms-auto text-xs text-muted-foreground">
                {formatDateTime(message.createdAt)}
              </span>
            </div>
            <p className="whitespace-pre-line text-sm">{message.body}</p>
          </div>
        ))}

        <Can permission="service.ticket.update">
          <div className="space-y-2 border-t pt-4">
            <div className="flex gap-1 rounded-lg border p-0.5 w-fit">
              <button
                onClick={() => setIsInternal(false)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                  !isInternal ? 'bg-secondary font-medium' : 'text-muted-foreground',
                )}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Reply to client
              </button>
              <button
                onClick={() => setIsInternal(true)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                  isInternal ? 'bg-amber-100 font-medium text-amber-900' : 'text-muted-foreground',
                )}
              >
                <StickyNote className="h-3.5 w-3.5" />
                Internal note
              </button>
            </div>

            {canned.length > 0 && !isInternal && (
              <Select
                className="h-8 w-auto text-xs"
                value=""
                onChange={(event) => {
                  const template = canned.find((item) => item.id === event.target.value);
                  if (!template) return;
                  setBody(template.body);
                  if (template.setStatus) setSetStatusTo(template.setStatus);
                }}
              >
                <option value="">Insert a canned reply…</option>
                {canned.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            )}

            <Textarea
              rows={4}
              placeholder={isInternal ? 'A note only the team can see…' : 'Your reply to the client…'}
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />

            <div className="flex flex-wrap items-center gap-2">
              <Select
                className="h-8 w-auto text-xs"
                value={setStatusTo}
                onChange={(event) => setSetStatusTo(event.target.value)}
              >
                <option value="">Leave the status alone</option>
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    Set to {TICKET_STATUS_LABELS[status].toLowerCase()}
                  </option>
                ))}
              </Select>

              <Button
                size="sm"
                className="ms-auto"
                disabled={reply.isPending || body.trim().length === 0}
                onClick={async () => {
                  setError(null);
                  try {
                    await reply.mutateAsync({
                      body,
                      isInternal,
                      setStatus: setStatusTo || undefined,
                    });
                    setBody('');
                    setSetStatusTo('');
                  } catch (caught) {
                    setError(
                      caught instanceof ApiRequestError ? caught.message : 'Could not send that.',
                    );
                  }
                }}
              >
                <Send className="h-3.5 w-3.5" />
                {isInternal ? 'Add note' : 'Send reply'}
              </Button>
            </div>

            <FormError message={error} />
          </div>
        </Can>
      </CardContent>
    </Card>
  );
}

function EscalateDialog({ ticket, onClose }: { ticket: Ticket; onClose: () => void }) {
  const escalate = useEscalateTicket(ticket.id);
  const { data: users = [] } = useUserOptions();
  const [reason, setReason] = useState('');
  const [toUserId, setToUserId] = useState('');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Escalate {ticket.number}</DialogTitle>
          <DialogDescription>
            This raises the priority and tells the support managers. The reason is what they will
            read first.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Why</Label>
            <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Reassign to (optional)</Label>
            <Select value={toUserId} onChange={(event) => setToUserId(event.target.value)}>
              <option value="">Keep the current assignee</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={escalate.isPending || reason.trim().length < 3}
            onClick={async () => {
              await escalate.mutateAsync({ reason, toUserId: toUserId || undefined });
              onClose();
            }}
          >
            Escalate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConvertDialog({ ticket, onClose }: { ticket: Ticket; onClose: () => void }) {
  const convert = useConvertTicket(ticket.id);
  const { data: projects } = useProjects({ limit: 100, status: 'ACTIVE' });
  const [target, setTarget] = useState<'BUG' | 'CHANGE_REQUEST' | 'DEAL'>('BUG');
  const [projectId, setProjectId] = useState(ticket.projectId ?? '');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const needsProject = target !== 'DEAL';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Convert {ticket.number}</DialogTitle>
          <DialogDescription>
            The link back to this ticket is kept, so a developer picking up the bug can see whose
            production system it is.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Convert into</Label>
            <Select value={target} onChange={(event) => setTarget(event.target.value as never)}>
              <option value="BUG">A bug on a project</option>
              <option value="CHANGE_REQUEST">A change request</option>
              <option value="DEAL">A sales opportunity</option>
            </Select>
          </div>

          {needsProject && (
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
                <option value="">Choose a project</option>
                {(projects?.data ?? []).map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.code} — {project.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input
              value={title}
              placeholder={`${ticket.company.name}: ${ticket.subject}`}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={convert.isPending || (needsProject && !projectId)}
            onClick={async () => {
              setError(null);
              try {
                await convert.mutateAsync({
                  target,
                  projectId: needsProject ? projectId : undefined,
                  title: title || undefined,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not convert it.',
                );
              }
            }}
          >
            Convert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
